import React, { useState, useRef } from 'react'
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  Box,
  Text,
  VStack,
  HStack,
  Image,
  IconButton,
  Flex,
  Progress,
  useToast,
} from '@chakra-ui/react'
import { DeleteIcon, AddIcon } from '@chakra-ui/icons'
import { MdFileUpload } from 'react-icons/md'

const ModalUploadFotos = ({ isOpen, onClose, isLoading, produto, onEnviarFotos }) => {
  const [arquivos, setArquivos] = useState([])
  const [arrastando, setArrastando] = useState(false)
  const [progresso, setProgresso] = useState(0)
  const inputRef = useRef(null)
  const toast = useToast()

  const MAX_ARQUIVOS = 3
  const TAMANHO_MAXIMO = 5 * 1024 * 1024 // 5MB

  React.useEffect(() => {
    if (isOpen) {
      setArquivos([])
      setProgresso(0)
    }
  }, [isOpen])

  const handleDragEnter = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setArrastando(true)
  }

  const handleDragLeave = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setArrastando(false)
  }

  const handleDragOver = (e) => {
    e.preventDefault()
    e.stopPropagation()
    if (!arrastando) {
      setArrastando(true)
    }
  }

  const validarArquivo = (arquivo) => {
    if (arquivo.size > TAMANHO_MAXIMO) {
      toast({
        title: 'Arquivo muito grande',
        description: `O arquivo ${arquivo.name} excede o tamanho máximo de 5MB`,
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
      return false
    }

    if (!arquivo.type.startsWith('image/')) {
      toast({
        title: 'Tipo de arquivo inválido',
        description: 'Apenas imagens são permitidas',
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
      return false
    }

    return true
  }

  const processarArquivos = (files) => {
    const novosArquivos = Array.from(files).filter(validarArquivo)

    if (arquivos.length + novosArquivos.length > MAX_ARQUIVOS) {
      toast({
        title: 'Limite excedido',
        description: `Você pode enviar no máximo ${MAX_ARQUIVOS} imagens`,
        status: 'warning',
        duration: 5000,
        isClosable: true,
      })

      const espacoDisponivel = MAX_ARQUIVOS - arquivos.length
      setArquivos([...arquivos, ...novosArquivos.slice(0, espacoDisponivel)])
    } else {
      setArquivos([...arquivos, ...novosArquivos])
    }
  }

  const handleDrop = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setArrastando(false)

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      processarArquivos(e.dataTransfer.files)
    }
  }

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      processarArquivos(e.target.files)
    }
  }

  const handleClickInput = () => {
    inputRef.current.click()
  }

  const removerArquivo = (index) => {
    setArquivos(arquivos.filter((_, i) => i !== index))
  }

  console.log(produto)

  const handleEnviar = async () => {
    if (arquivos.length === 0) {
      toast({
        title: 'Nenhuma imagem selecionada',
        description: 'Por favor, selecione ao menos uma imagem para enviar',
        status: 'warning',
        duration: 5000,
        isClosable: true,
      })
      return
    }

    let contador = 0
    const intervalo = setInterval(() => {
      contador += 10
      setProgresso(contador)
      if (contador >= 100) {
        clearInterval(intervalo)
      }
    }, 200)

    try {
      const formData = new FormData()
      //       id_produto: (int) id do produto(tabela AD_PRECADASTROPROD)
      // codigo_produto (int) codprod(tabela AD_PRECOFORNECEDOR)
      // id_usuario: (int) id do usuário que irá efetuar a ação
      // imagem[]: (array) array com as imagens do produto
      formData.append('id_produto', produto.id)
      formData.append('codigo_produto', produto.cod_inno)
      formData.append('id_usuario', produto.id_usuario)

      arquivos.forEach((arquivo) => {
        formData.append('imagem[]', arquivo)
      })

      await onEnviarFotos(formData)

      onClose()
    } catch (error) {
      toast({
        title: 'Erro ao enviar imagens',
        description: error.message || 'Houve um problema ao enviar suas imagens',
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
    } finally {
      clearInterval(intervalo)
      setProgresso(0)
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Upload de Fotos para {produto?.ref_fornecedor}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            {/* Área de arrastar e soltar */}
            <Box
              border="2px dashed"
              borderColor={arrastando ? 'blue.500' : 'gray.300'}
              borderRadius="md"
              display="flex"
              alignItems="center"
              justifyContent="center"
              flexDirection="column"
              p={8}
              textAlign="center"
              bg={arrastando ? 'blue.50' : 'transparent'}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              transition="all 0.2s"
              cursor="pointer"
              onClick={handleClickInput}
              position="relative"
            >
              <input type="file" ref={inputRef} onChange={handleFileChange} accept="image/*" multiple style={{ display: 'none' }} />

              <Box as={MdFileUpload} size="50px" color={arrastando ? 'blue.500' : 'gray.400'} mb={2} />
              <Text fontWeight="bold" mb={2}>
                Arraste as imagens ou clique para selecionar
              </Text>
              <Text fontSize="sm" color="gray.500">
                Máximo de {MAX_ARQUIVOS} imagens (PNG, JPG ou JPEG até 5MB cada)
              </Text>
            </Box>

            {/* Lista de arquivos selecionados */}
            {arquivos.length > 0 && (
              <Box>
                <Text fontWeight="bold" mb={2}>
                  {arquivos.length} de {MAX_ARQUIVOS} imagens selecionadas
                </Text>
                <VStack spacing={2} align="stretch">
                  {arquivos.map((arquivo, index) => (
                    <Flex key={index} p={2} bg="gray.50" borderRadius="md" alignItems="center" justifyContent="space-between">
                      <HStack>
                        <Image src={URL.createObjectURL(arquivo)} alt={`Preview ${index}`} boxSize="40px" objectFit="cover" borderRadius="md" />
                        <Text noOfLines={1} maxW="200px">
                          {arquivo.name}
                        </Text>
                        <Text fontSize="xs" color="gray.500">
                          {(arquivo.size / 1024).toFixed(1)} KB
                        </Text>
                      </HStack>
                      <IconButton
                        icon={<DeleteIcon />}
                        size="sm"
                        colorScheme="red"
                        variant="ghost"
                        onClick={() => removerArquivo(index)}
                        aria-label="Remover arquivo"
                      />
                    </Flex>
                  ))}
                </VStack>
              </Box>
            )}

            {/* Botão para adicionar mais fotos quando já tem algumas */}
            {arquivos.length > 0 && arquivos.length < MAX_ARQUIVOS && (
              <Button leftIcon={<AddIcon />} onClick={handleClickInput} size="sm" w="fit-content" colorScheme="blue" variant="outline">
                Adicionar mais fotos
              </Button>
            )}

            {/* Barra de progresso */}
            {progresso > 0 && (
              <Box>
                <Text mb={1} fontSize="sm">
                  Enviando...
                </Text>
                <Progress value={progresso} size="sm" colorScheme="blue" />
              </Box>
            )}
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose} isDisabled={isLoading}>
            Cancelar
          </Button>
          <Button
            colorScheme="blue"
            onClick={handleEnviar}
            isLoading={isLoading}
            loadingText="Enviando..."
            isDisabled={arquivos.length === 0 || isLoading}
          >
            Enviar {arquivos.length > 0 ? `(${arquivos.length})` : ''}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

export default ModalUploadFotos
